import { useEffect, useState } from 'react';

/**
 * Height of the visible viewport while the on-screen keyboard is open, or
 * null when the normal layout height (100dvh) is already correct.
 *
 * iOS never resizes the layout viewport for the keyboard — it pans the page
 * instead, which scrolls the app bar off-screen and leaves the composer in
 * an unpredictable spot (worse in a saved-to-homescreen app). Shrinking the
 * app shell to visualViewport.height keeps the header visible and the
 * composer seated directly above the keyboard. Android resizes the layout
 * viewport natively, so the >100px gap check stays false there and this
 * hook stays inert.
 */
export function useVisualViewportHeight(enabled: boolean): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHeight(null);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // >100px gap = keyboard; smaller gaps are browser-chrome collapse.
      const keyboardOpen = window.innerHeight - vv.height > 100;
      setHeight(keyboardOpen ? Math.round(vv.height) : null);
      if (keyboardOpen) window.scrollTo(0, 0);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setHeight(null);
    };
  }, [enabled]);

  return height;
}
